/**
 * 整理券ページの申し込みフォーム。
 *
 * 商品のオプションごとにセレクトがあり、前のセレクトで選んだ値に合う枠だけで次のセレクトの選択肢を組み直す。
 * 最後のセレクトで枠（バリアント）が1つに決まるため、そのバリアントIDを hidden input に反映してカート追加を有効にする。
 * 枠ごとの空き状況（空きあり / 残りわずか / 受付終了）は Liquid 側で判定済みの値を使う。
 * 送信直前にカートを取り直し、同じ商品が既に入っていれば申し込まずに再読み込みする。
 */
(() => {
  const SELECTORS = {
    root: '.Reservation',
    form: '.Reservation_form',
    slots: '[data-seiriken-slots]',
    option: '[data-seiriken-option]',
    variant: '[data-seiriken-variant]',
    submit: '[data-seiriken-submit]',
    submitLabel: '[data-seiriken-submit-label]',
  };

  const readSlots = (root) => {
    const node = root.querySelector(SELECTORS.slots);
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      // 枠のデータが読めない状態では申し込ませない（定員超過は当日回復できないため）
      return [];
    }
  };

  const createOption = (value, label, disabled) => {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = label;
    option.disabled = disabled;
    return option;
  };

  const initReservation = (root) => {
    if (root.dataset.seirikenReady === 'true') return;

    const form = root.querySelector(SELECTORS.form);
    const optionSelects = [...root.querySelectorAll(SELECTORS.option)];
    const variantInput = root.querySelector(SELECTORS.variant);
    const submit = root.querySelector(SELECTORS.submit);
    const submitLabel = root.querySelector(SELECTORS.submitLabel);
    if (!form || optionSelects.length === 0 || !variantInput || !submit || !submitLabel) return;

    const slots = readSlots(root);
    if (slots.length === 0) return;

    const labelReady = submit.dataset.labelReady;
    const labelIdle = submitLabel.textContent.trim();

    const clearSelection = () => {
      variantInput.value = '';
      submit.disabled = true;
      submitLabel.textContent = labelIdle;
    };

    const lastIndex = optionSelects.length - 1;

    // fromIndex 番目以降のセレクトを、それより前のセレクトで選ばれた値に合う枠だけで組み直す
    const renderSelects = (fromIndex) => {
      optionSelects.slice(fromIndex).forEach((select, offset) => {
        const index = fromIndex + offset;
        const chosenValues = optionSelects.slice(0, index).map((previous) => previous.value);
        const isWaiting = chosenValues.some((value) => !value);
        const candidates = isWaiting
          ? []
          : slots.filter((slot) => chosenValues.every((value, i) => slot.options[i] === value));
        const options = [createOption('', select.dataset.placeholder, false)];

        if (index === lastIndex) {
          // 枠が1つに決まるセレクトなので、値にはバリアントIDを持たせ、空き状況を併記する
          candidates.forEach((slot) => {
            options.push(createOption(String(slot.id), slot.label, !slot.selectable));
          });
          select.replaceChildren(...options);
        } else {
          const values = [...new Set(candidates.map((slot) => slot.options[index]))];
          values.forEach((value) => {
            options.push(createOption(value, value, false));
          });
          select.replaceChildren(...options);
          // 選べる値が1つだけなら（例: 1日だけの開催）、選ぶ手間を省いて次のセレクトへ進める
          if (values.length === 1) select.value = values[0];
        }
        select.disabled = isWaiting;
      });
    };

    optionSelects.forEach((select, index) => {
      select.addEventListener('change', () => {
        if (index < lastIndex) {
          renderSelects(index + 1);
          clearSelection();
          return;
        }
        const selected = slots.find((slot) => String(slot.id) === select.value);
        if (!selected || !selected.selectable) {
          clearSelection();
          return;
        }
        variantInput.value = String(selected.id);
        submit.disabled = false;
        submitLabel.textContent = labelReady;
      });
    });

    const productId = Number(root.dataset.productId);

    // 「申し込み済み」は表示時点のカートで判定しているため、別タブで同じ商品をカートに入れた後でも
    // このタブからは申し込めてしまう。送信直前にカートを取り直して確かめる。
    const isProductInCart = async () => {
      try {
        const cartUrl = `${window.Shopify?.routes?.root ?? '/'}cart.js`;
        const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
        if (!response.ok) return false;
        const cart = await response.json();
        return cart.items.some((item) => item.product_id === productId);
      } catch (error) {
        // カートを取得できないときは申し込みを止めない（フェイルオープン）。重複はカートページでも検知する
        return false;
      }
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      // 連打で同じ枠が二重に申し込まれるのを防ぐ
      submit.disabled = true;

      if (await isProductInCart()) {
        // 「カートに入っています」の案内は Liquid 側で描画するため、再読み込みしてその表示に切り替える
        window.location.reload();
        return;
      }
      // form.submit() は submit イベントを発火しないため、このハンドラは再実行されない
      form.submit();
    });

    renderSelects(0);
    clearSelection();

    root.dataset.seirikenReady = 'true';
  };

  const initAll = () => {
    document.querySelectorAll(SELECTORS.root).forEach(initReservation);
  };

  initAll();
  // テーマエディタでセクションを編集し直したときに初期化をやり直す
  document.addEventListener('shopify:section:load', initAll);
})();
